import { useRef } from "react";
import { formatSecondsAsClock, parseClockToSeconds } from "../lib/clockHelpers";

/**
 * PMA-COCK-006: live Tiempo Fuera readout derived from the cumulative global
 * clock while a timeout is active (same pattern as `useCockpitVarDerivedState`),
 * so it changes in the same render as `global-cumulative-clock` and by the
 * same whole seconds. `useTimeoutTimer`'s own value (refreshed on an unrelated
 * 1000 ms tick) is used only for the anchor and whenever no timeout is active.
 *
 * Must be called AFTER `useMatchTimer` produced `globalClock`; its output never
 * feeds back into `useMatchTimer`.
 */
interface UseCockpitTimeoutDerivedStateParams {
  isTimeoutActive: boolean;
  timeoutTimeSeconds: number;
  globalClock: string;
  /** True once the match object is present (globalClock is real, not the placeholder). */
  isMatchLoaded: boolean;
  /**
   * False while the global clock is stopped (operator stop, before kickoff).
   * The display then falls back to `timeoutTimeSeconds`, which keeps counting.
   */
  isGlobalClockRunning: boolean;
}

interface CockpitTimeoutDerivedState {
  timeoutDisplaySeconds: number;
  timeoutDisplayClock: string;
}

/** A forward jump larger than this between two renders is a refetch/drift correction. */
const REANCHOR_FORWARD_JUMP_SECONDS = 2;

interface AnchorState {
  anchored: boolean;
  anchorGlobalSeconds: number;
  anchorTotalSeconds: number;
  lastGlobalSeconds: number | null;
  wasRunning: boolean;
  lastDisplaySeconds: number;
}

export const useCockpitTimeoutDerivedState = ({
  isTimeoutActive,
  timeoutTimeSeconds,
  globalClock,
  isMatchLoaded,
  isGlobalClockRunning,
}: UseCockpitTimeoutDerivedStateParams): CockpitTimeoutDerivedState => {
  // Anchors live in a ref updated during render (idempotent for a given set of
  // inputs) so the derived value is correct in the SAME render the global
  // clock changes; state/effects would lag one render behind.
  const anchorRef = useRef<AnchorState>({
    anchored: false,
    anchorGlobalSeconds: 0,
    anchorTotalSeconds: 0,
    lastGlobalSeconds: null,
    wasRunning: true,
    lastDisplaySeconds: 0,
  });

  let timeoutDisplaySeconds = timeoutTimeSeconds;

  if (isMatchLoaded) {
    const anchor = anchorRef.current;
    const globalSeconds = parseClockToSeconds(globalClock);

    if (!isTimeoutActive) {
      anchor.anchored = false;
      anchor.lastGlobalSeconds = globalSeconds;
      anchor.wasRunning = isGlobalClockRunning;
      anchor.lastDisplaySeconds = 0;
    } else if (!isGlobalClockRunning) {
      // Global clock stopped: the timer's own total keeps advancing. Never
      // fall below what was already shown (the timer refreshes on a 1 s tick
      // and can trail the clock-derived value by under a second).
      timeoutDisplaySeconds = Math.max(
        timeoutTimeSeconds,
        anchor.lastDisplaySeconds,
      );
      anchor.anchored = false;
      anchor.wasRunning = false;
      anchor.lastGlobalSeconds = globalSeconds;
      anchor.lastDisplaySeconds = timeoutDisplaySeconds;
    } else {
      const last = anchor.lastGlobalSeconds;
      const jumped =
        last !== null &&
        (globalSeconds < last ||
          globalSeconds - last > REANCHOR_FORWARD_JUMP_SECONDS);
      if (!anchor.anchored || jumped) {
        // Also the rising edge after a stop: anchor at the value on screen.
        anchor.anchored = true;
        anchor.anchorGlobalSeconds = globalSeconds;
        anchor.anchorTotalSeconds = anchor.wasRunning
          ? timeoutTimeSeconds
          : Math.max(timeoutTimeSeconds, anchor.lastDisplaySeconds);
      }
      anchor.wasRunning = true;
      anchor.lastGlobalSeconds = globalSeconds;
      timeoutDisplaySeconds =
        anchor.anchorTotalSeconds +
        Math.max(0, globalSeconds - anchor.anchorGlobalSeconds);
      // A stale anchor (e.g. tab resumed after a suspension, anchored to an
      // old memoized total) is corrected once a total more than 1 s ahead of
      // the display arrives. Only ever moves forward; a total within 1 s
      // behind/ahead keeps the anchor (no oscillation).
      if (timeoutTimeSeconds - timeoutDisplaySeconds > 1) {
        anchor.anchorGlobalSeconds = globalSeconds;
        anchor.anchorTotalSeconds = timeoutTimeSeconds;
        timeoutDisplaySeconds = timeoutTimeSeconds;
      }
      anchor.lastDisplaySeconds = timeoutDisplaySeconds;
    }
  }

  return {
    timeoutDisplaySeconds,
    timeoutDisplayClock: formatSecondsAsClock(timeoutDisplaySeconds),
  };
};
